import { Icon, IconSize } from "@/shared/components";
import { cn } from "@/shared/utils";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

function TooltipProvider({ delayDuration = 0, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
    return <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />;
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
    return (
        <TooltipProvider>
            <TooltipPrimitive.Root data-slot="tooltip" {...props} />
        </TooltipProvider>
    );
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
    return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

type TooltipContentProps = React.ComponentProps<typeof TooltipPrimitive.Content> & {
    arrowClassName?: string;
};

function TooltipContent({ className, arrowClassName, sideOffset = 0, children, ...props }: TooltipContentProps) {
    return (
        <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
                data-slot="tooltip-content"
                sideOffset={sideOffset}
                side="bottom"
                className={cn(
                    "bg-default-inverse text-content-default-inverse animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 text-s z-50 w-fit max-w-[204px] origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-2 text-balance",
                    className
                )}
                {...props}
            >
                {children}
                <TooltipPrimitive.Arrow
                    className={cn(
                        "bg-default-inverse fill-default-inverse z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]",
                        arrowClassName
                    )}
                />
            </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
    );
}

type InfoTooltipProps = {
    children: React.ReactNode;
    className?: string;
    iconSize?: IconSize;
};

function InfoTooltip({ children, className, iconSize }: InfoTooltipProps) {
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Icon name="info" size={iconSize} className="text-content-emphasis" />
            </TooltipTrigger>
            <TooltipContent className={className}>{children}</TooltipContent>
        </Tooltip>
    );
}

export { InfoTooltip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
